import { gql, useMutation, useQuery } from '@apollo/client';
import { Button, useToast } from '@chakra-ui/react';
import * as React from 'react';

import { useSignUpPrompt } from '@/hooks/useSignUpPrompt';
import { logError } from '@/imports/logging/ClientLogger';
import { organization_followed, organization_unfollowed } from '@/analytics';

import {
  FollowButton_CreateMutation,
  FollowButton_CreateMutationVariables,
} from './graphql-types/FollowButton_CreateMutation';
import { FollowButton_CurrentUser } from './graphql-types/FollowButton_CurrentUser';
import {
  FollowButton_DeleteMutation,
  FollowButton_DeleteMutationVariables,
} from './graphql-types/FollowButton_DeleteMutation';
import {
  FollowButton_OrganizationQuery,
  FollowButton_OrganizationQueryVariables,
} from './graphql-types/FollowButton_OrganizationQuery';

const CURRENT_USER_QUERY = gql`
  query FollowButton_CurrentUser {
    currentUser {
      _id
    }
  }
`;

const CREATE_MUTATION = gql`
  mutation FollowButton_CreateMutation(
    $input: CreateUserOrganizationLinkInput!
  ) {
    createUserOrganizationLink(input: $input) {
      data {
        _id
        organization {
          _id
          numFollowers
        }
      }
    }
  }
`;

const DELETE_MUTATION = gql`
  mutation FollowButton_DeleteMutation(
    $input: DeleteUserOrganizationLinkInput!
  ) {
    deleteUserOrganizationLink(input: $input) {
      data {
        _id
        organization {
          _id
          numFollowers
        }
      }
    }
  }
`;

const ORGANIZATION_QUERY = gql`
  query FollowButton_OrganizationQuery($input: SingleOrganizationInput!) {
    organization(input: $input) {
      result {
        _id
        displayName
        defaultDigestPref
        userOrganizationLink {
          _id
          digestPreference
        }
      }
    }
  }
`;

type FollowButtonProps = {
  organizationId: string;
};

export const FollowButton: React.FC<FollowButtonProps> = (props) => {
  const toast = useToast();

  const currentUserQuery =
    useQuery<FollowButton_CurrentUser>(CURRENT_USER_QUERY);

  const organizationQuery = useQuery<
    FollowButton_OrganizationQuery,
    FollowButton_OrganizationQueryVariables
  >(ORGANIZATION_QUERY, {
    variables: {
      input: {
        id: props.organizationId,
      },
    },
  });

  const [createUserOrganizationLink, createUserOrganizationLinkResult] =
    useMutation<
      FollowButton_CreateMutation,
      FollowButton_CreateMutationVariables
    >(CREATE_MUTATION, {
      refetchQueries: ['FollowButton_OrganizationQuery'],
      awaitRefetchQueries: true,
    });

  const [deleteUserOrganizationLink, deleteUserOrganizationLinkResult] =
    useMutation<
      FollowButton_DeleteMutation,
      FollowButton_DeleteMutationVariables
    >(DELETE_MUTATION, {
      refetchQueries: ['FollowButton_OrganizationQuery'],
      awaitRefetchQueries: true,
    });

  const signUpPrompt = useSignUpPrompt();

  if (currentUserQuery.loading || organizationQuery.loading) {
    return null;
  }

  if (currentUserQuery.error) {
    logError(currentUserQuery.error);
  }

  if (organizationQuery.error) {
    logError(organizationQuery.error);
  }

  const followOrganization = async (): Promise<void> => {
    if (!currentUserQuery.data?.currentUser) {
      signUpPrompt.showPrompt();
      return;
    }

    try {
      await createUserOrganizationLink({
        variables: {
          input: {
            data: {
              userId: currentUserQuery.data.currentUser._id,
              organizationId: props.organizationId,
            },
          },
        },
      });

      organization_followed({
        entity_type: 'organization',
        entity_id: props.organizationId,
        organization_name: organizationQuery.data?.organization?.result?.displayName || '',
      });

      toast({
        description: `Successfully followed ${organizationQuery.data?.organization?.result?.displayName}`,
        status: 'success',
      });
    } catch (err) {
      toast({
        description: `Failed to follow ${organizationQuery.data?.organization?.result?.displayName}`,
        status: 'error',
      });
      logError(err);
    }
  };

  const unfollowOrganization = async (): Promise<void> => {
    const userOrgLinkId =
      organizationQuery.data?.organization?.result?.userOrganizationLink?._id;

    if (!currentUserQuery.data?.currentUser || !userOrgLinkId) {
      return;
    }

    try {
      await deleteUserOrganizationLink({
        variables: {
          input: {
            id: userOrgLinkId,
          },
        },
      });

      organization_unfollowed({
        entity_type: 'organization',
        entity_id: props.organizationId,
        organization_name: organizationQuery.data?.organization?.result?.displayName || '',
      });

      toast({
        description: `Successfully unfollowed ${organizationQuery.data?.organization?.result?.displayName}.`,
        status: 'success',
      });
    } catch (err) {
      toast({
        description: `Failed to unfollow ${organizationQuery.data?.organization?.result?.displayName}.`,
        status: 'error',
      });
      logError(err);
    }
  };

  const isFollowing =
    !!organizationQuery.data?.organization?.result?.userOrganizationLink;

  const isDisabled =
    currentUserQuery.loading ||
    signUpPrompt.loading ||
    organizationQuery.loading;

  return (
    <>
      {signUpPrompt.promptModal}
      <Button
        flexShrink="0"
        size="sm"
        isLoading={
          createUserOrganizationLinkResult.loading ||
          deleteUserOrganizationLinkResult.loading
        }
        isDisabled={isDisabled}
        onClick={isFollowing ? unfollowOrganization : followOrganization}
        variant={isFollowing ? 'outline' : 'solid'}
        colorScheme={isFollowing ? 'gray' : 'primary'}
      >
        {isFollowing ? 'Following' : 'Follow'}
      </Button>
    </>
  );
};
